import React from 'react';
import { Box, Typography } from '@mui/material';

const minutes = (ms) => {
  const sign = ms < 0 ? '-' : '';
  const total = Math.round(Math.abs(ms) / 1000);
  return `${sign}${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

/**
 * Who was in the call when, as Bidsy's judge reads it (admin panel plan
 * RPT-17): the fan, the creator, the time together, and the call's 80% and
 * 20% lines. The panel draws what Bidsy answers and works out nothing; all
 * times are in ms after the call time.
 */
export default function CallTimeline({ timeline, totals = null }) {
  if (!timeline) {
    return <Typography variant="body2" color="text.secondary">The call&apos;s timeline could not be read.</Typography>;
  }
  const { length_ms: lengthMs } = timeline;
  const stretches = [...timeline.fan, ...timeline.creator];
  const from = Math.min(0, ...stretches.map((s) => s.start));
  const to = Math.max(lengthMs, ...stretches.map((s) => s.end));
  const span = Math.max(1, to - from);
  const at = (ms) => `${((ms - from) / span) * 100}%`;
  const width = (s) => `${(Math.max(0, s.end - s.start) / span) * 100}%`;
  const completeAt = timeline.complete_line_ms ?? (lengthMs * timeline.complete_line_percent) / 100;

  const row = (label, list, color) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
      <Typography variant="caption" sx={{ width: 64 }}>{label}</Typography>
      <Box sx={{ position: 'relative', flex: 1, height: 14, bgcolor: 'action.hover', borderRadius: 1 }} aria-label={`${label} in the call`}>
        {list.map((s) => (
          <Box
            key={`${s.start}-${s.end}`}
            title={`${minutes(s.start)} to ${minutes(s.end)}${s.how ? ` (${s.how})` : ''}`}
            sx={{ position: 'absolute', left: at(s.start), width: width(s), top: 0, bottom: 0, bgcolor: color, borderRadius: 1 }}
          />
        ))}
        <Box sx={{ position: 'absolute', left: at(0), top: -2, bottom: -2, borderLeft: '1px dashed', borderColor: 'text.secondary' }} />
        <Box sx={{ position: 'absolute', left: at(completeAt), top: -2, bottom: -2, borderLeft: '2px solid', borderColor: 'success.main' }} />
        <Box sx={{ position: 'absolute', left: at(lengthMs), top: -2, bottom: -2, borderLeft: '1px dashed', borderColor: 'text.secondary' }} />
      </Box>
    </Box>
  );

  return (
    <Box data-testid="call-timeline">
      {row('Fan', timeline.fan, 'info.main')}
      {row('Creator', timeline.creator, 'secondary.main')}
      {row('Together', timeline.together, 'success.main')}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
        Dashed lines: the call time ({minutes(0)}) and its booked end ({minutes(lengthMs)}). Green line: {timeline.complete_line_percent}% of the call ({minutes(completeAt)}).
        The call ends early if the creator is away for {timeline.creator_away_limit_percent}% of it.
      </Typography>
      {totals ? (
        <Typography variant="body2" sx={{ mt: 1 }}>
          Delivered together {minutes(totals.deliveredMs)} of {minutes(lengthMs)} · fan away {minutes(totals.fanAbsentMs)} · creator away {minutes(totals.creatorAbsentMs)}
          {timeline.creator_away_limit_ms != null ? ` of the ${minutes(timeline.creator_away_limit_ms)} allowed` : ''}
        </Typography>
      ) : null}
    </Box>
  );
}
